/**
 * Groups sensor events by their base type by removing the status suffix from the type string.
 *
 * This function processes an array of sensor event objects (typically cooktop sensor events)
 * and groups them by their base type. The type string is split by dots, the last segment
 * (status) is removed, and events are grouped under the resulting base type. Events within
 * each group are sorted by timestamp according to the specified order.
 *
 * @param {Array<Object>} input - Array of sensor event objects to group
 * @param {string} input[].type - Event type in dot notation (e.g., "cooktop.sensor.on")
 * @param {Array<Object>} input[].events - Array of event data objects
 * @param {Object} opts - Configuration options
 * @param {Array<string>} [opts.timeFieldPriority=['utc_timestamp_inms', 'time', 'recv_time']] -
 *   Priority order of timestamp fields to use for sorting
 * @param {string} [opts.order='desc'] - Sort order for events ('desc' or 'asc')
 *
 * @returns {Array<Object>} Array of grouped event objects
 * @returns {string} return[].type - The base type without status suffix
 * @returns {Array<Object>} return[].events - Sorted array of events with status field added
 * @returns {string} return[].events[].status - The status extracted from original type
 *
 * @example
 * const input = [
 *   {
 *     type: "cooktop.sensor.on",
 *     events: [
 *       { utc_timestamp_inms: 1638360000000, value: 1 },
 *       { utc_timestamp_inms: 1638360001000, value: 2 }
 *     ]
 *   },
 *   {
 *     type: "cooktop.sensor.off",
 *     events: [
 *       { utc_timestamp_inms: 1638360002000, value: 0 }
 *     ]
 *   }
 * ];
 *
 * const result = groupByBaseType(input);
 * // Returns:
 * // [
 * //   {
 * //     type: "cooktop.sensor",
 * //     events: [
 * //       { utc_timestamp_inms: 1638360002000, value: 0, status: "off" },
 * //       { utc_timestamp_inms: 1638360001000, value: 2, status: "on" },
 * //       { utc_timestamp_inms: 1638360000000, value: 1, status: "on" }
 * //     ]
 * //   }
 * // ]
 */

import {
  COOKTOP_CATEGORY,
  SENSORS,
  DEVICE_STATES,
  DEVICE_STATE_PROPERTY,
  getRadarOccupancyState,
  SMART_PLUG_POWER_CONSUMPTION_THRESHOLD,
  DEVICE_EVENT_TYPES_CHARTS,
  INCONTINENCE_SENSOR_STATES,
  SENSOR_VARIANTS,
  EVENT_TYPES,
} from '@/constants/device';
import { getSensorVariant } from '@/utilities/helpers/commonHelper';

export const getFlushSensorActive = (event, eventType) => {
  const stateProperty =
    eventType === EVENT_TYPES.FLUSH_SENSOR.ACCESSORY_STATE_BLE_TOILET_FLUSH_SENSOR
      ? DEVICE_STATE_PROPERTY.toilet_flush_sensor_ble
      : DEVICE_STATE_PROPERTY.toilet_flush_sensor;

  return event?.[stateProperty] ?? event?.active;
};

export const groupByBaseType = (input = [], opts = {}) => {
  const { timeFieldPriority = ['utc_timestamp_inms', 'time', 'recv_time'], order = 'desc' } = opts;

  const getTs = (ev) => {
    for (const f of timeFieldPriority) {
      const v = ev?.[f];
      if (v == null) continue;
      if (typeof v === 'number') return v;
      if (typeof v === 'string') {
        if (/^\d+$/.test(v)) return parseInt(v, 10);
        const p = Date.parse(v);
        if (!Number.isNaN(p)) return p;
      }
    }
    return 0;
  };

  const map = input.reduce((m, item) => {
    if (!item?.type) return m;
    const parts = item.type.split('.');
    const status = parts.pop();
    const base = parts.join('.');

    const evs = (item.events || []).map((ev) => ({ ...ev, status, __t: getTs(ev) }));

    if (!m.has(base)) m.set(base, { type: base, events: [] });
    m.get(base).events.push(...evs);
    return m;
  }, new Map());

  return Array.from(map.values()).map((g) => ({
    ...g,
    events: g.events
      .slice()
      .sort((a, b) => (order === 'desc' ? b.__t - a.__t : a.__t - b.__t))
      // eslint-disable-next-line no-unused-vars
      .map(({ __t, ...rest }) => rest),
  }));
};

export const convertEventsToSegments = (userEvents, { windowStart, windowEnd, sensorDetails }) => {
  if (!Array.isArray(userEvents) || !userEvents?.length || !windowStart || !windowEnd) {
    return [];
  }

  let sorted = [...userEvents].sort((a, b) => new Date(a.recv_time) - new Date(b.recv_time));

  if (
    userEvents.length > 0 &&
    userEvents[0]?.message_type === DEVICE_EVENT_TYPES_CHARTS.smart_plug[0]
  ) {
    let allTimestamps = [];

    for (let event of userEvents) {
      try {
        const oneGroup = JSON.parse(event.readings);
        if (Array.isArray(oneGroup)) {
          allTimestamps = allTimestamps.concat(oneGroup);
        }
      } catch (e) {
        // Ignore invalid JSON
        continue;
      }
    }

    sorted = allTimestamps
      .sort((a, b) => a.timestamp - b.timestamp)
      .map((e) => ({
        recv_time: e.timestamp,
        recent_energy: e.value,
      }));
  }

  const sensorVariant = getSensorVariant(sensorDetails);

  if (sensorDetails.devicetype === SENSORS.SMART_HOME.OCCUPANCY_SENSOR) {
    const targetMessageType =
      sensorVariant === SENSOR_VARIANTS.INCONTINENCE
        ? EVENT_TYPES.OCCUPANCY_SENSOR.ACCESSORY_STATE_UPDATE_OCCUPANCY_SENSOR_WET
        : EVENT_TYPES.OCCUPANCY_SENSOR.ACCESSORY_STATE_OCCUPANCY_SENSOR_ACTIVE;

    sorted = sorted.filter((event) => event?.message_type === targetMessageType);
  }

  let segments = [];

  for (let i = 0; i < sorted.length - 1; i++) {
    const current = sorted[i];
    const next = sorted[i + 1];

    // If next event is missing, we skip (by loop condition)
    const start = new Date(current.recv_time);
    const end = new Date(next.recv_time);

    // Skip if completely outside window
    if (start > windowEnd || end < windowStart) continue;

    // Clamp inside window
    const clampedStart = start < windowStart ? windowStart : start;

    let clampedEnd = end > windowEnd ? windowEnd : end;

    if (clampedEnd <= clampedStart) continue;

    let state = null;
    let occupancyState = null;
    let zoneName = null;

    if (sensorDetails.devicetype === SENSORS.SMART_HOME.DOOR_WINDOW_SENSOR) {
      state = current.open
        ? DEVICE_STATES.DOOR_WINDOW_SENSOR.OPEN
        : DEVICE_STATES.DOOR_WINDOW_SENSOR.CLOSED;
    }

    if (sensorDetails.devicetype === SENSORS.SMART_HOME.OCCUPANCY_SENSOR) {
      if (sensorVariant === SENSOR_VARIANTS.INCONTINENCE) {
        state = current.wet ? INCONTINENCE_SENSOR_STATES.WET : INCONTINENCE_SENSOR_STATES.DRY;
      } else {
        state = current.active
          ? DEVICE_STATES.OCCUPANCY_SENSOR.OCCUPIED
          : DEVICE_STATES.OCCUPANCY_SENSOR.VACANT;
      }
    }

    if (sensorDetails.devicetype === SENSORS.SMART_HOME.RADAR_SENSOR) {
      state = current.presence_type || null;
      occupancyState = getRadarOccupancyState(current.presence_type);
      zoneName = current.zone_name || null;
    }

    if (sensorDetails.devicetype === SENSORS.SMART_HOME.COOKTOP_SENSOR) {
      const presenceStatus = (current[COOKTOP_CATEGORY.PRESENCE] || '').toString().toLowerCase();
      state =
        presenceStatus === 'detected'
          ? DEVICE_STATES.OCCUPANCY_SENSOR.OCCUPIED
          : DEVICE_STATES.OCCUPANCY_SENSOR.VACANT;
      occupancyState = state;
    }

    if (sensorDetails.devicetype === SENSORS.SMART_HOME.TOILET_FLUSH_SENSOR) {
      const isFlushActive = getFlushSensorActive(current, current.message_type);
      state = isFlushActive
        ? DEVICE_STATES.FLUSH_SENSOR.FLUSHED
        : DEVICE_STATES.FLUSH_SENSOR.NOT_FLUSHED;

      if (isFlushActive) {
        const oneMinuteAfter = new Date(clampedStart);
        oneMinuteAfter.setMinutes(oneMinuteAfter.getMinutes() + 1);
        clampedEnd = oneMinuteAfter > windowEnd ? windowEnd : oneMinuteAfter;
      }
    }

    if (sensorDetails.devicetype === SENSORS.SMART_HOME.SMART_PLUG) {
      state =
        current.recent_energy > SMART_PLUG_POWER_CONSUMPTION_THRESHOLD
          ? DEVICE_STATES.SMART_PLUG.ON
          : DEVICE_STATES.SMART_PLUG.OFF;
    }

    // Merge consecutive same-state segments
    const prev = segments[segments.length - 1];
    const canMergeRadar =
      sensorDetails.devicetype !== SENSORS.SMART_HOME.RADAR_SENSOR || prev?.zoneName === zoneName;

    if (
      prev &&
      prev.state === state &&
      prev.endTs === clampedStart.toISOString() &&
      canMergeRadar
    ) {
      prev.endTs = clampedEnd.toISOString();
      continue;
    }

    segments.push({
      startTs: clampedStart.toISOString(),
      endTs: clampedEnd.toISOString(),
      state,
      occupancyState,
      zoneName,
    });
  }

  const getState = (event) => {
    switch (sensorDetails.devicetype) {
      case SENSORS.SMART_HOME.DOOR_WINDOW_SENSOR:
        return event.open
          ? DEVICE_STATES.DOOR_WINDOW_SENSOR.OPEN
          : DEVICE_STATES.DOOR_WINDOW_SENSOR.CLOSED;

      case SENSORS.SMART_HOME.OCCUPANCY_SENSOR:
        if (sensorVariant === SENSOR_VARIANTS.INCONTINENCE) {
          return event.wet ? INCONTINENCE_SENSOR_STATES.WET : INCONTINENCE_SENSOR_STATES.DRY;
        }
        return event.active
          ? DEVICE_STATES.OCCUPANCY_SENSOR.OCCUPIED
          : DEVICE_STATES.OCCUPANCY_SENSOR.VACANT;

      case SENSORS.SMART_HOME.RADAR_SENSOR:
        return event.presence_type || null;

      case SENSORS.SMART_HOME.COOKTOP_SENSOR: {
        const presenceStatus = (event[COOKTOP_CATEGORY.PRESENCE] || '').toString().toLowerCase();
        return presenceStatus === 'detected'
          ? DEVICE_STATES.OCCUPANCY_SENSOR.OCCUPIED
          : DEVICE_STATES.OCCUPANCY_SENSOR.VACANT;
      }

      case SENSORS.SMART_HOME.TOILET_FLUSH_SENSOR:
        return getFlushSensorActive(event, event.message_type)
          ? DEVICE_STATES.FLUSH_SENSOR.FLUSHED
          : DEVICE_STATES.FLUSH_SENSOR.NOT_FLUSHED;

      case SENSORS.SMART_HOME.SMART_PLUG:
        return event.on ? DEVICE_STATES.SMART_PLUG.ON : DEVICE_STATES.SMART_PLUG.OFF;

      default:
        return null;
    }
  };

  // Handle last event → extend till now (timezone-safe)
  const lastEvent = sorted[sorted.length - 1];
  const now = new Date(Date.now());

  if (lastEvent) {
    const start = new Date(lastEvent.recv_time);

    if (!(start > windowEnd || now < windowStart)) {
      const clampedStart = start < windowStart ? windowStart : start;
      let clampedEnd = now > windowEnd ? windowEnd : now;

      if (clampedEnd > clampedStart) {
        let state = getState(lastEvent);
        const occupancyState =
          sensorDetails.devicetype === SENSORS.SMART_HOME.RADAR_SENSOR
            ? getRadarOccupancyState(lastEvent.presence_type)
            : null;
        const zoneName =
          sensorDetails.devicetype === SENSORS.SMART_HOME.RADAR_SENSOR
            ? lastEvent.zone_name || null
            : null;

        if (
          sensorDetails.devicetype === SENSORS.SMART_HOME.TOILET_FLUSH_SENSOR &&
          getFlushSensorActive(lastEvent, lastEvent.message_type)
        ) {
          const oneMinuteAfter = new Date(clampedStart);
          oneMinuteAfter.setMinutes(oneMinuteAfter.getMinutes() + 1);
          clampedEnd = oneMinuteAfter > windowEnd ? windowEnd : oneMinuteAfter;
        }

        const prev = segments[segments.length - 1];
        const canMergeRadar =
          sensorDetails.devicetype !== SENSORS.SMART_HOME.RADAR_SENSOR ||
          prev?.zoneName === zoneName;

        if (
          prev &&
          prev.state === state &&
          prev.endTs === clampedStart.toISOString() &&
          canMergeRadar
        ) {
          prev.endTs = clampedEnd.toISOString();
        } else {
          segments.push({
            startTs: clampedStart.toISOString(),
            endTs: clampedEnd.toISOString(),
            state,
            occupancyState,
            zoneName,
          });
        }
      }
    }
  }

  return segments;
};

export const roundUpToNextHour = (date) => {
  const d = new Date(date);
  d.setMinutes(0, 0, 0);

  if (date.getMinutes() !== 0 || date.getSeconds() !== 0) {
    d.setHours(d.getHours() + 1);
  }
  return d;
};

export const formatTime = (timestamp, timezone = 'UTC', is12h, format = 'HH:mm') => {
  const date = new Date(timestamp);

  return date.toLocaleTimeString('en-GB', {
    hour: '2-digit',
    minute: format.includes('mm') ? '2-digit' : undefined,
    second: format.includes('ss') ? '2-digit' : undefined,
    hour12: !is12h,
    timeZone: timezone,
  });
};
